import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { defaultLocale } from '@/constants/locales'
import { useExperimentContext } from '@/experimentation'
import { GuildAccessNotch } from '@/molecules/GuildAccessNotch'
import { WatchHoverModal } from '@/organisms/WatchHoverModal'
import { LinkViewModel } from '@/services/RenderService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import useWindowSize from '@/utils/useWindowSize'

interface LargePortraitLinkProps {
  link: LinkViewModel
  className?: string
  isGuildMember?: boolean
}

export function LargePortraitLink(props: LargePortraitLinkProps) {
  const track = useSafeTrack()
  const [isHovered, setIsHovered] = useState(false)
  const [canShowHover, setCanShowHover] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const { innerWidth } = useWindowSize()
  const { getFeatureValue } = useExperimentContext()
  const { locale } = useLocale()
  const { className, isGuildMember = false, link } = props

  const handleClick = React.useCallback(() => {
    if (link.track) track(link.track?.eventName, link.track?.payload)
  }, [link.track, track])

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  useEffect(() => {
    if (innerWidth > 1023 && locale === defaultLocale) {
      const canShowHoverModal = getFeatureValue('watch-hover-modal-desktop', false)
      if (canShowHoverModal) {
        setCanShowHover(true)
      }
    }
  }, [innerWidth, getFeatureValue, locale])

  if (!link.imageUrl) return null

  return (
    <div
      onClick={handleClick}
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative"
    >
      <InternalLink
        href={link.linkUrl}
        aria-label={`project-tile-${link.name}`}
        className={classNames(
          'relative inline-block h-fit overflow-hidden rounded-lg duration-[400ms] ease-in-out hover:z-10 md:hover:scale-[1.05]',
          className,
        )}
      >
        <Image
          aria-label={link.alt}
          alt={link.alt}
          src={getCloudinaryImageUrl({
            path: link.imageUrl,
            transforms: 'w_294',
          })}
          height={490}
          width={294}
          className="rounded-lg bg-black"
        />
        <GuildAccessNotch isInEarlyAccess={link.isInEarlyAccess} isGuildMember={isGuildMember} />
      </InternalLink>
      {canShowHover && <WatchHoverModal guid={link?.id} isVisible={isHovered} projectSlug={link?.projectSlug} />}
    </div>
  )
}
